"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.planDatabaseRowInsertion = planDatabaseRowInsertion;
exports.databaseRowShardId = databaseRowShardId;
exports.nextDatabaseShardIndex = nextDatabaseShardIndex;
exports.databaseDirectoryShardIndexHolds = databaseDirectoryShardIndexHolds;
exports.nextDatabaseRowShardIndex = nextDatabaseRowShardIndex;
exports.fitDatabaseRowShardPrefix = fitDatabaseRowShardPrefix;
exports.partitionDatabaseRowShardRows = partitionDatabaseRowShardRows;
exports.planDatabaseRowOrderChange = planDatabaseRowOrderChange;
exports.canonicalDatabaseRowOrder = canonicalDatabaseRowOrder;
exports.planDatabaseRowOrderSplices = planDatabaseRowOrderSplices;
exports.planDatabaseRowShardMerges = planDatabaseRowShardMerges;
/** Shared metadata/byte planning; no document loads or mutations. */
function planDatabaseRowInsertion(shards, index) {
    if (!Number.isSafeInteger(index) || index < 0)
        throw new RangeError('Database row index must be a non-negative safe integer');
    let totalRows = 0;
    for (const shard of shards) {
        if (!Number.isSafeInteger(shard.rowCount) || shard.rowCount < 0)
            throw new RangeError('Database shard row count is invalid');
        totalRows += shard.rowCount;
        if (!Number.isSafeInteger(totalRows))
            throw new RangeError('Database row count exceeds the supported range');
    }
    const target = Math.min(index, totalRows);
    let start = 0;
    for (let shardIndex = 0; shardIndex < shards.length; shardIndex++) {
        const count = shards[shardIndex].rowCount;
        if (target < start + count || shardIndex === shards.length - 1)
            return { shardIndex, localIndex: target - start, totalRows };
        start += count;
    }
    return { shardIndex: -1, localIndex: 0, totalRows };
}
/** The row shard ID with numeric address `index`, as every writer names it. */
function databaseRowShardId(index) {
    return String(index).padStart(5, '0');
}
/**
 * Highest numeric row-shard index a directory handed out: the one it stores
 * or the highest of `shards`, whichever is higher. A writer names its next
 * shard `databaseRowShardId(index + 1)`.
 */
function nextDatabaseShardIndex(directory) {
    return Math.max(directory.nextShardIndex ?? 0, nextDatabaseRowShardIndex(directory.shards));
}
/**
 * Whether a written directory head keeps its shard index past every shard id
 * handed out: never below the stored index and at least every numeric id of
 * the shards in the parts the write changes. An older client adds shards
 * without moving the index on; a writer reading only some parts would then
 * hand out a stored id. A head without an index is read whole by every
 * writer; once stored, the index stays.
 */
function databaseDirectoryShardIndexHolds(storedIndex, writtenIndex, writtenShards) {
    if (writtenIndex === undefined)
        return storedIndex === undefined;
    return (writtenIndex >= (storedIndex ?? 0) &&
        writtenIndex >= nextDatabaseRowShardIndex(writtenShards));
}
/** Numeric shard IDs are stable addresses, never manifest array positions. */
function nextDatabaseRowShardIndex(shards) {
    let maximum = 0;
    for (const { shardId } of shards) {
        if (!/^\d+$/.test(shardId))
            continue;
        const numeric = Number(shardId);
        if (!Number.isSafeInteger(numeric) || numeric >= Number.MAX_SAFE_INTEGER)
            throw new RangeError('Database numeric shard ID exceeds the supported range');
        maximum = Math.max(maximum, numeric);
    }
    return maximum;
}
/** A prior capacity is a search hint, never a byte-limit exemption. Exact
 * probes bracket the new prefix even when row widths change between packets. */
function fitDatabaseRowShardPrefix(remaining, targetBytes, hardLimitBytes, measure, priorCapacity = 1) {
    if (!Number.isSafeInteger(remaining) ||
        remaining < 1 ||
        !Number.isSafeInteger(priorCapacity) ||
        priorCapacity < 1 ||
        !Number.isSafeInteger(targetBytes) ||
        targetBytes < 1 ||
        !Number.isSafeInteger(hardLimitBytes) ||
        hardLimitBytes < targetBytes)
        throw new RangeError('Database shard byte limits are invalid');
    const cache = new Map();
    const bytes = (count) => {
        const prior = cache.get(count);
        if (prior !== undefined)
            return prior;
        const value = measure(count);
        if (!Number.isSafeInteger(value) || value < 0)
            throw new RangeError('Database shard encoded byte count is invalid');
        cache.set(count, value);
        return value;
    };
    let lower = 0, upper = remaining + 1;
    const guess = Math.min(priorCapacity, remaining);
    if (bytes(guess) > targetBytes)
        upper = guess;
    else {
        lower = guess;
        // Adjacent exact probe makes stable-width repeated packets two probes.
        let candidate = Math.min(remaining, lower + 1);
        while (lower < remaining) {
            if (bytes(candidate) > targetBytes) {
                upper = candidate;
                break;
            }
            lower = candidate;
            candidate = Math.min(remaining, lower * 2);
        }
    }
    while (lower + 1 < upper) {
        const middle = Math.floor((lower + upper) / 2);
        if (bytes(middle) <= targetBytes)
            lower = middle;
        else
            upper = middle;
    }
    if (lower)
        return lower;
    if (bytes(1) > hardLimitBytes)
        throw new RangeError('Database row exceeds the row-shard hard byte limit');
    return 1;
}
/**
 * Exponential probes keep temporary sizing documents near one shard; binary
 * refinement avoids rebuilding/encoding a growing document for every row.
 * A single row may exceed the target, but must fit the unchanged hard limit.
 */
function partitionDatabaseRowShardRows(rows, targetBytes, hardLimitBytes, measure, options = {}) {
    if (!Number.isSafeInteger(targetBytes) ||
        targetBytes <= 0 ||
        !Number.isSafeInteger(hardLimitBytes) ||
        hardLimitBytes < targetBytes)
        throw new RangeError('Database shard byte limits are invalid');
    const groups = [];
    let offset = 0;
    let priorCapacity = options.priorCapacity ?? 1;
    while (offset < rows.length) {
        const remaining = rows.length - offset;
        const bytes = (count) => {
            const measured = measure(rows.slice(offset, offset + count), groups.length);
            if (!Number.isSafeInteger(measured) || measured < 0)
                throw new RangeError('Database shard encoded byte count is invalid');
            return measured;
        };
        const count = fitDatabaseRowShardPrefix(remaining, targetBytes, hardLimitBytes, bytes, priorCapacity);
        priorCapacity = count;
        groups.push(rows.slice(offset, offset + count));
        options.onSelected?.(groups[groups.length - 1], groups.length - 1);
        offset += count;
    }
    // A mutation of a full shard must not leave that shard full and create a
    // one-row tail on every subsequent prepend/move. Balance only the last pair;
    // exact byte probes still decide whether both halves are admissible.
    if (options.balanceTail && groups.length >= 2) {
        const left = groups[groups.length - 2];
        const right = groups[groups.length - 1];
        if (left.length > right.length + 1) {
            const pair = [...left, ...right];
            const middle = Math.ceil(pair.length / 2);
            const halves = [pair.slice(0, middle), pair.slice(middle)];
            const fits = halves.every((part, index) => {
                const bytes = measure(part, groups.length - 2 + index);
                if (!Number.isSafeInteger(bytes) || bytes < 0)
                    throw new RangeError('Database shard encoded byte count is invalid');
                return bytes <= (part.length === 1 ? hardLimitBytes : targetBytes);
            });
            if (fits) {
                halves.forEach((part, index) => options.onSelected?.(part, groups.length - 2 + index));
                groups.splice(groups.length - 2, 2, ...halves);
            }
        }
    }
    return groups.length ? groups : [[]];
}
/** Minimal ordered-list delta used by both Yjs runtime adapters. */
function planDatabaseRowOrderChange(previous, next) {
    if (next.some(id => typeof id !== 'string' || !id) ||
        new Set(next).size !== next.length)
        throw new RangeError('Database row order must contain unique non-empty IDs.');
    let prefix = 0;
    while (prefix < next.length &&
        prefix < previous.length &&
        next[prefix] === previous[prefix])
        prefix++;
    let suffix = 0;
    while (suffix < next.length - prefix &&
        suffix < previous.length - prefix &&
        next[next.length - 1 - suffix] === previous[previous.length - 1 - suffix])
        suffix++;
    return {
        index: prefix,
        deleteCount: previous.length - prefix - suffix,
        insert: next.slice(prefix, next.length - suffix),
    };
}
/**
 * The row order of a shard. Writers on different peers can insert the same
 * deterministic row ID at once, so the merged Y.Array may hold it twice; the
 * first occurrence in array order places the row.
 */
function canonicalDatabaseRowOrder(stored) {
    return [...new Set(stored)];
}
/**
 * Changes that turn a stored row order into `next`. Each later occurrence of
 * an ID is deleted on its own, highest index first and never reinserted, so
 * peers that repair the same order at once delete the same Y.Array items. One
 * splice then applies the change of the write to the canonical order.
 */
function planDatabaseRowOrderSplices(stored, next) {
    const seen = new Set();
    const splices = [];
    stored.forEach((id, index) => {
        if (seen.has(id))
            splices.unshift({ index, deleteCount: 1, insert: [] });
        else
            seen.add(id);
    });
    const change = planDatabaseRowOrderChange([...seen], next);
    if (change.deleteCount || change.insert.length)
        splices.push(change);
    return splices;
}
/** Shards a write left below `mergeBelowBytes` join an order-neighbour when
 * the joined size stays within `targetBytes`; empty shards leave the manifest.
 * Only shards the write changed are sources. The last shard is never one: it
 * is the append tail and holds the highest shard ID, so removing it would let
 * `nextDatabaseRowShardIndex` reuse the ID of a document that has history.
 * `followed` says that more shards of the database come after `shards`, for
 * a writer that holds only some of them: its last one is no tail then.
 * Every shard takes part in at most one merge per write. Pure metadata
 * planning: each writer executes the plan with its own primitives.
 */
function planDatabaseRowShardMerges(shards, changedShardIds, mergeBelowBytes, targetBytes, followed = false) {
    const merges = [];
    const busy = new Set();
    const last = followed ? shards.length : shards.length - 1;
    for (const [index, shard] of shards.entries()) {
        if (index === last ||
            !changedShardIds.has(shard.shardId) ||
            busy.has(shard.shardId))
            continue;
        if (shard.rowCount === 0) {
            busy.add(shard.shardId);
            merges.push({
                sourceShardId: shard.shardId,
                targetShardId: null,
                position: 'append',
            });
            continue;
        }
        if (shard.encodedBytes >= mergeBelowBytes)
            continue;
        const fits = (neighbour) => neighbour &&
            neighbour.rowCount > 0 &&
            !busy.has(neighbour.shardId) &&
            shard.encodedBytes + neighbour.encodedBytes <= targetBytes;
        const next = shards[index + 1];
        const previous = shards[index - 1];
        const target = fits(next) ? next : fits(previous) ? previous : undefined;
        if (!target)
            continue;
        busy.add(shard.shardId);
        busy.add(target.shardId);
        merges.push({
            sourceShardId: shard.shardId,
            targetShardId: target.shardId,
            position: target === next ? 'prepend' : 'append',
        });
    }
    return merges;
}
